import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deleteQuiz, getQuizAuthoring, saveQuiz } from "@/api/quiz";
import { Button, Card } from "@/components";
import { useLang } from "@/i18n/LanguageContext";

type I18n = Record<string, string>;
interface EditOption {
  text_i18n: I18n;
  is_correct: boolean;
}
interface EditQuestion {
  prompt_i18n: I18n;
  kind: "single" | "multiple";
  options: EditOption[];
}

const LANGS = ["nl", "en"] as const;
type Lang = (typeof LANGS)[number];

function newQuestion(): EditQuestion {
  return { prompt_i18n: {}, kind: "single", options: [{ text_i18n: {}, is_correct: true }, { text_i18n: {}, is_correct: false }] };
}

export function QuizEditor({ moduleId }: { moduleId: string }) {
  const qc = useQueryClient();
  const { t } = useLang();
  const quiz = useQuery({ queryKey: ["quiz", moduleId], queryFn: ({ signal }) => getQuizAuthoring(moduleId, signal) });

  const [threshold, setThreshold] = useState(70);
  const [questions, setQuestions] = useState<EditQuestion[]>([]);
  const [editLang, setEditLang] = useState<Lang>("nl");
  const [error, setError] = useState<string | null>(null);
  const exists = !!quiz.data;

  useEffect(() => {
    if (quiz.data) {
      setThreshold(quiz.data.pass_threshold);
      setQuestions(quiz.data.questions.map((q) => ({
        prompt_i18n: q.prompt_i18n ?? (q.prompt ? { nl: q.prompt } : {}),
        kind: q.kind,
        options: q.options.map((o) => ({ text_i18n: o.text_i18n ?? (o.text ? { nl: o.text } : {}), is_correct: !!o.is_correct })),
      })));
    }
  }, [quiz.data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => saveQuiz(moduleId, {
      pass_threshold: threshold,
      questions: questions.map((q) => ({
        prompt_i18n: q.prompt_i18n,
        kind: q.kind,
        options: q.options.map((o) => ({ text_i18n: o.text_i18n, is_correct: o.is_correct })),
      })),
    }),
    onSuccess: () => { setError(null); qc.invalidateQueries({ queryKey: ["quiz", moduleId] }); },
    onError: (e) => setError(e instanceof Error ? e.message : t("Opslaan mislukt", "Could not save")),
  });
  const remove = useMutation({
    mutationFn: () => deleteQuiz(moduleId),
    onSuccess: () => { setQuestions([]); qc.invalidateQueries({ queryKey: ["quiz", moduleId] }); },
  });

  function patchQ(i: number, patch: Partial<EditQuestion>) {
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  }
  function setPrompt(qi: number, value: string) {
    setQuestions((qs) => qs.map((q, i) => (i === qi ? { ...q, prompt_i18n: { ...q.prompt_i18n, [editLang]: value } } : q)));
  }
  function setOptText(qi: number, oi: number, value: string) {
    setQuestions((qs) => qs.map((q, i) => (i !== qi ? q : { ...q, options: q.options.map((o, j) => (j === oi ? { ...o, text_i18n: { ...o.text_i18n, [editLang]: value } } : o)) })));
  }
  function setCorrect(qi: number, oi: number, value: boolean) {
    setQuestions((qs) =>
      qs.map((q, i) => {
        if (i !== qi) return q;
        if (q.kind === "single") return { ...q, options: q.options.map((o, j) => ({ ...o, is_correct: j === oi ? value : false })) };
        return { ...q, options: q.options.map((o, j) => (j === oi ? { ...o, is_correct: value } : o)) };
      }),
    );
  }

  if (quiz.isLoading) return null;

  return (
    <Card>
      <p className="card-num">{t("KENNISCHECK (QUIZ)", "KNOWLEDGE CHECK (QUIZ)")}</p>
      {error && <div className="form-error">{error}</div>}

      {questions.length === 0 ? (
        <div>
          <p className="no-data-message">{t("Geen kennischeck. Voeg er één toe om deze stap pas af te ronden bij een geslaagde toets.", "No knowledge check. Add one to gate this step on a passing score.")}</p>
          <Button variant="outline" onClick={() => setQuestions([newQuestion()])}>{t("Kennischeck toevoegen", "Add knowledge check")}</Button>
        </div>
      ) : (
        <>
          <div className="lang-tabs">
            {LANGS.map((l) => (
              <button key={l} type="button" className={`lang-tab ${editLang === l ? "active" : ""}`} onClick={() => setEditLang(l)}>{l.toUpperCase()}</button>
            ))}
          </div>
          <p style={{ color: "var(--grey-text)", fontSize: 12.5, marginBottom: 10 }}>
            {t("Juiste antwoorden en vraagtype gelden voor beide talen.", "Correct answers and question type apply to both languages.")}
          </p>

          <div className="field" style={{ maxWidth: 220 }}>
            <label htmlFor="thr">{t("Slaagdrempel (%)", "Pass threshold (%)")}</label>
            <input id="thr" type="number" min={0} max={100} value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} />
          </div>

          {questions.map((q, qi) => (
            <div key={qi} style={{ borderTop: "1px solid var(--line)", paddingTop: 14, marginTop: 8 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div className="field" style={{ flex: 1, marginBottom: 8 }}>
                  <label>{t("Vraag", "Question")} {qi + 1} ({editLang.toUpperCase()})</label>
                  <input value={q.prompt_i18n[editLang] ?? ""} onChange={(e) => setPrompt(qi, e.target.value)} placeholder={t("Vraagtekst", "Question text")} />
                </div>
                <div className="field" style={{ marginBottom: 8 }}>
                  <label>{t("Type", "Type")}</label>
                  <select value={q.kind} onChange={(e) => patchQ(qi, { kind: e.target.value as "single" | "multiple" })}>
                    <option value="single">{t("Eén juist", "Single answer")}</option>
                    <option value="multiple">{t("Meerdere juist", "Multiple answers")}</option>
                  </select>
                </div>
                <button className="icon-btn" title={t("Vraag verwijderen", "Delete question")} onClick={() => setQuestions((qs) => qs.filter((_, i) => i !== qi))}>×</button>
              </div>
              {q.options.map((o, oi) => (
                <div key={oi} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                  <input type={q.kind === "single" ? "radio" : "checkbox"} checked={!!o.is_correct} onChange={(e) => setCorrect(qi, oi, e.target.checked)} title={t("Juist antwoord", "Correct answer")} />
                  <input style={{ flex: 1, border: "1px solid var(--line)", borderRadius: 4, padding: "8px 10px" }} value={o.text_i18n[editLang] ?? ""} onChange={(e) => setOptText(qi, oi, e.target.value)} placeholder={`${t("Antwoord", "Answer")} ${oi + 1} (${editLang.toUpperCase()})`} />
                  <button className="icon-btn" title={t("Antwoord verwijderen", "Delete answer")} onClick={() => setQuestions((qs) => qs.map((qq, i) => (i === qi ? { ...qq, options: qq.options.filter((_, j) => j !== oi) } : qq)))}>×</button>
                </div>
              ))}
              <Button variant="outline" onClick={() => patchQ(qi, { options: [...q.options, { text_i18n: {}, is_correct: false }] })} style={{ padding: "6px 12px", fontSize: 13 }}>+ {t("Antwoord", "Answer")}</Button>
            </div>
          ))}

          <div className="toolbar" style={{ marginTop: 16 }}>
            <Button variant="outline" onClick={() => setQuestions((qs) => [...qs, newQuestion()])}>+ {t("Vraag", "Question")}</Button>
            <div className="spacer" />
            {exists && <Button variant="outline" onClick={() => remove.mutate()} disabled={remove.isPending}>{t("Verwijderen", "Delete")}</Button>}
            <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? t("Opslaan…", "Saving…") : t("Kennischeck opslaan", "Save knowledge check")}</Button>
          </div>
        </>
      )}
    </Card>
  );
}
